import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appConfig } from '../config/appConfig';

const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);

    const isValid =
      username.trim() === appConfig.admin.username && password.trim() === appConfig.admin.password;

    if (!isValid) {
      setStatus('Invalid credentials.');
      return;
    }

    const tryCheck = async (url: string) => {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const contentType = res.headers.get('content-type') || '';
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status} ${res.statusText}: ${text.substring(0, 120)}`);
      }
      if (!contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Non-JSON response: ${text.substring(0, 120)}`);
      }
      return res.json();
    };

    try {
      setLoading(true);
      setStatus('Testing database connection...');
      try {
        const data = await tryCheck('/api/health/db');
        if (data?.ok) {
          setStatus('Database connection successful. Redirecting...');
          // Store admin session
          sessionStorage.setItem('adminAuthenticated', 'true');
          // Redirect to admin dashboard after a short delay
          setTimeout(() => {
            navigate('/admin/dashboard');
          }, 1500);
          return;
        }
      } catch (_proxyErr) {
        // Proxy failed; try direct API URL (production or development)
        const apiUrl = process.env.NODE_ENV === 'production' 
          ? 'https://raise-backend-254054690264.asia-south1.run.app/api/health/db'
          : 'http://localhost:4000/api/health/db';
        const data = await tryCheck(apiUrl);
        if (data?.ok) {
          setStatus('Database connection successful. Redirecting...');
          // Store admin session
          sessionStorage.setItem('adminAuthenticated', 'true');
          // Redirect to admin dashboard after a short delay
          setTimeout(() => {
            navigate('/admin/dashboard');
          }, 1500);
          return;
        }
      }
      setStatus('Database connection failed: Unknown error');
    } catch (err: any) {
      setStatus(`Database connection failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--secondary-pearl)]">
      <div className="card w-full max-w-md">
        <div className="card-content">
          <h1 className="heading-secondary mb-6 text-center">Admin Login</h1>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-2">
              <label className="body-small" htmlFor="username">Username</label>
              <input
                id="username"
                className="border border-[var(--neutral-medium-gray)] rounded-md px-3 py-2 focus:outline-none"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="grid gap-2">
              <label className="body-small" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className="border border-[var(--neutral-medium-gray)] rounded-md px-3 py-2 focus:outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <button disabled={loading} className="btn btn-primary mt-2" type="submit">
              {loading ? 'Checking...' : 'Login'}
            </button>
          </form>
          {status && (
            <div className="mt-4 body-regular">
              {status}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;


